import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KotTicket, Product, ProductVariant, TransactionItem, Transaction, TransportInfo, DiscountSlab } from '../types';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import { computeBillTotals } from '../services/billing';
import {
  printReceiptAuto,
  printDeliveryNote,
  mailtoBillUrl,
  maybePrintCoupon,
  dayNightOf,
} from '../services/receipt';
import { buildKotHtml } from '../services/escpos';
import { printKotRouted } from '../services/kotRouting';
import { publishLedSnapshot } from '../services/customerDisplay';
import { HeldBill, getHeldBills, saveHeldBills } from '../services/heldBills';
import { lockLandscape } from '../services/nativeOrientation';
import { useRestaurant } from '../hooks/useRestaurant';
import PaymentModal from '../components/PaymentModal';
import CustomerPickerDialog from '../components/CustomerPickerDialog';
import BarcodeScanner from '../components/BarcodeScanner';
import './Bill.css';

type OrderType = 'dinein' | 'parcel' | 'self';
type PriceMode = 'retail' | 'wholesale';
type PayMode = 'Cash' | 'Credit' | 'Digital' | 'Split';

// A bill line carries an extra `sentQty`: how much of this line has already been
// fired to the kitchen as a KOT. The sent portion is locked (no edit / remove /
// reprint); only the unsent remainder (quantity − sentQty) is editable and is
// what the next KOT fires. Plain (non-restaurant) billing leaves it undefined.
type BillLine = TransactionItem & { sentQty?: number };
const unsentOf = (i: BillLine) => Math.max(0, i.quantity - (i.sentQty ?? 0));
const isLocked = (i: BillLine) => (i.sentQty ?? 0) > 0;

// First letter of each word, lowercased — "Veg Fried Rice" -> "vfr", so typing
// an acronym finds the product without spelling out the whole name.
const nameInitials = (name: string) => name.trim().split(/\s+/).map((w) => w[0] ?? '').join('').toLowerCase();

// How long a bill has been sitting on hold, e.g. "3m", "1h 20m" — so an
// operator can spot an order that's been parked too long.
const agoLabel = (ms: number): string => {
  const mins = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

interface Pending {
  productId: string;
  // The variant the entry card is working on, when the line is a variant line.
  // Carried so committing an edit lands on the right size/colour row.
  variantId?: string;
  name: string;
  qty: string;
  price: string;
  taxRatePercent: number;
  // Multi-unit / weighable capture for the entry card.
  weighable?: boolean;
  unitGroupId?: string;
  unitName?: string;
  basePrice?: number; // selling price in the base unit, before sub-unit scaling
  bags?: string;
  // True when the entry card is editing an existing bill line (vs. adding new):
  // committing then replaces the line's qty/price instead of accumulating.
  editing?: boolean;
}

// Faithful reconstruction of res/layout/bill_fragment.xml — landscape 50/50 two-pane.
// LEFT: [Add Customer + | UDN] row, search, green entry card, toggles, item list, Last Bill + NET AMT.
// RIGHT: action bar, product buttons, details (Payment Mode, customer card, discount, totals).
const Bill: React.FC = () => {
  const { config, products, customers, salesmen, coupons, sales, unitGroups, attributes, categories: categoryMaster, servicePoints, recipes, recordSale, addCashFlow, isOn, choice } = usePos();

  // app_setting_layout.xml feature toggles that shape the billing screen.
  const showWholesale = isOn('wholesale');
  const showDineInParcel = isOn('dinein_parcel');
  const showHoldList = isOn('show_hold_list_button');
  const showDiscountUi = isOn('discount_amount_ui_option');
  const showBillDetails = isOn('show_bill_details_section'); // bill-details panel — hidden by default
  const disableSaveBill = isOn('disable_save_button'); // "D - Disable Save Bill Button"
  const disablePrintBill = isOn('disable_print_button'); // "E - Disable Print Bill Button"
  const showCustomer = isOn('customer_details');
  const hideProductList = isOn('do_not_show_product_list');
  const hideSearchBox = isOn('hide_product_search_box'); // "N - Hide Product Search Box"
  const allowManualPrice = isOn('manual_price') || isOn('open_price');
  // "D - Product List Style" gates whether the style radio applies at all.
  const listStyle = isOn('product_list_style_enable') ? choice('product_list_style') || 'Normal' : 'Normal';
  const taxInclusive = isOn('tax_inclusive'); // "7 - TAX SETTING / A - Inclusive Tax"
  const categoryDiscountOn = isOn('category_discount'); // per-line % from the category master
  const hotKeysNoSort = isOn('hot_keys_disable'); // "F - Disable sorting in Hot keys"
  const sortByName = isOn('product_sort_by_name');
  const stockMinus = isOn('stock_getting_minus'); // sell stockable items at ≤0 stock
  const showImages = isOn('show_images'); // product images in the grid
  const hideItemName = isOn('item_name_hide'); // hide name when image shown
  const multiCouponOn = isOn('multi_coupon'); // apply several coupons to one bill
  const billSettlementOn = isOn('bill_settlement'); // save unsettled, settle later
  const salesmanProductWise = isOn('enable_salesman_product_wise'); // stamp salesman per line
  const barcodeScanDirectly = isOn('barcode_scan_direclty'); // act on scan without Enter
  const advanceOn = isOn('enable_customer_advance'); // record a customer advance from the bill
  const igstMode = isOn('isIGST'); // IGST regime vs CGST+SGST split
  const roundOffOn = isOn('isRoundOffAmt'); // round final total to nearest rupee
  const estimateModeOn = isOn('estimateMode'); // allow Estimate / proforma bills
  const nonGstMode = isOn('nonGST'); // non-GST outlet → no tax breakup
  const serviceChargeOn = isOn('service_charge'); // restaurant service charge field
  const deliveryNoteOn = isOn('deliveryNote'); // delivery challan / vehicle fields
  const udnOn = isOn('enable_udn'); // UDN box on the top row (hidden by default)

  // "1 — Billing Screen Option" extras.
  const allowDecimalQty = isOn('allow_decimal_qty'); // O - allow fractional quantities
  const reverseQty = isOn('reverse_qty'); // Amt box back-calculates Qty
  const showPrintAfterPay = isOn('show_print_buttons_after_pay'); // post-pay print dialog
  const openingBalanceOn = isOn('enable_opening_balance'); // opening-balance banner
  const openingAmountShow = isOn('enable_opening_balance_amount_set'); // suggest yesterday's cash
  const floatingKeyboard = isOn('enable_floating_keyboard'); // on-screen numeric keypad
  const networkIndicator = isOn('enable_network_connection'); // online/offline badge
  const counterOn = isOn('counter_one_two'); // Counter 1 & 2 selector
  const customerForce = isOn('enable_customer_forcefully'); // customer required to bill
  const transportOn = isOn('enable_transport_details'); // transport detail capture
  // "4 — Product Setting" extras.
  const regionalNames = isOn('language'); // show regional product names
  const threeCharSearch = isOn('enable_three_char_search'); // search from 3 chars
  const hideLineTotal = isOn('product_total_amount'); // "M - Disable Product Total Amt"
  const addOneTime = isOn('add_product_one_time'); // don't accumulate duplicate lines
  const variantsOn = isOn('variant'); // variant chooser
  const barcodeQty = isOn('search_with_only_barcode'); // "qty*barcode" entry
  // "6 — Category Setting".
  const catGrid = isOn('category_grideview_style');
  const catCols = choice('category_grid_count') || '8';
  const catVertical = isOn('category_view_vertical');
  const catRegional = isOn('category_regional');
  // "Keyboard Setting".
  const connectKeyboard = isOn('connect_keyboard'); // F2 search / F4 pay shortcuts
  const openSoftKeyboard = isOn('open_soft_keyboard'); // autofocus the search box
  // "Advance Settings / General".
  const emailBillOn = isOn('bill_format_send_to_email');
  const storeOn = isOn('enable_store'); // store badge on the bill header
  const quickDiscount = isOn('discount_directly_add_bill'); // one-tap % chips
  const a4Note = isOn('a4_delivery_note'); // A4 delivery note after pay
  const loadAnim = isOn('bill_screen_loading_style'); // grid fade-in style
  const premisesTax = isOn('premises_wise_tax'); // premise stamp on the bill
  const selfServiceOn = isOn('self_services'); // Self order type
  const showPremisesFirst = isOn('show_premises_first'); // premise chips up top
  const abcdSplit = isOn('abcd_table_split'); // A–D table suffixes
  const dayNightOn = isOn('enable_day_night'); // DAY/NIGHT badge + print
  const combosInBilling = isOn('enable_combo_add_billing'); // COMBO chip lists recipes

  // "15 — Table Settings" toggles that shape the billing screen.
  const enableTables = isOn('enable_tables');
  const tableForcefully = isOn('table_forcefully');
  const enterToken = isOn('enter_token_number');
  const askCaptain = isOn('ask_captain_details');

  // "9 — Salesman Setting" toggles.
  const enableSalesman = isOn('enable_salesman');
  const salesmanForce = isOn('enable_salesman_forcefully');

  // "16 — KOT Setting" toggles. The KOT action bar appears for restaurant-style
  // billing (tables on) or whenever any KOT feature is switched on.
  const kotRemarkOn = isOn('kot_remark');
  const showSaveKot = !isOn('disable_save_kot_button');
  const showPrintKot = !isOn('disable_print_kot_button');
  const kotReprint = isOn('kot_reprint_button');
  const noBillWithoutKot = isOn('no_bill_without_kot');
  const kotEnabled =
    enableTables || kotRemarkOn || kotReprint || noBillWithoutKot || isOn('disable_save_kot_button') || isOn('disable_print_kot_button');

  const rest = useRestaurant();
  const [items, setItems] = useState<BillLine[]>([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  // Sub-category drill-down within the active category ('All' = whole category).
  const [activeSubCategory, setActiveSubCategory] = useState('All');
  // Selecting a category resets the sub-category drill-down.
  const selectCategory = (c: string) => {
    setActiveCategory(c);
    setActiveSubCategory('All');
  };
  // MENU CARD (hot-keys best-seller grid) vs CATEGORY (chip-filtered grid) —
  // the Android btnHotKeys toggle. The button shows the view it switches TO.
  const [gridView, setGridView] = useState<'menu' | 'category'>('category');
  const [pending, setPending] = useState<Pending | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [showCustPicker, setShowCustPicker] = useState(false);
  const [udn, setUdn] = useState('');
  // Table / KOT bill context (driven by the Table & KOT app settings).
  const [table, setTable] = useState('');
  const [token, setToken] = useState('');
  const [captain, setCaptain] = useState('');
  const [salesman, setSalesman] = useState('');
  const [kotRemark, setKotRemark] = useState('');
  const [kotSaved, setKotSaved] = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [tableInput, setTableInput] = useState('');
  const [orderType, setOrderType] = useState<OrderType>('dinein');
  const [priceMode, setPriceMode] = useState<PriceMode>('retail');
  const [discAmt, setDiscAmt] = useState('');
  const [discPct, setDiscPct] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState('');
  const [couponMsg, setCouponMsg] = useState('');
  const [deliveryCharges, setDeliveryCharges] = useState('');
  const [servicePct, setServicePct] = useState('');
  const [serviceAmt, setServiceAmt] = useState('');
  // Estimate (proforma) bill — only togglable when estimateMode app setting is on.
  const [estimate, setEstimate] = useState(false);
  // Per-bill Non-GST / IGST overrides (Android ButtonGrid 28/30 print this one
  // bill as Non-GST / IGST). null = follow the global app-setting default.
  const [nonGstBill, setNonGstBill] = useState<boolean | null>(null);
  const [igstBill, setIgstBill] = useState<boolean | null>(null);
  const [extraCharges, setExtraCharges] = useState('');
  const [challanNo, setChallanNo] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [payMode, setPayMode] = useState<PayMode>('Cash');
  const [remark, setRemark] = useState('');
  // Seeded synchronously from IndexedDB (primed at boot) so held bills survive
  // a refresh; every change is persisted back in the effect below.
  const [heldBills, setHeldBills] = useState<HeldBill[]>(() => getHeldBills());
  useEffect(() => {
    saveHeldBills(heldBills);
  }, [heldBills]);
  const [showPayment, setShowPayment] = useState(false);
  const [pendingTx, setPendingTx] = useState<Transaction | null>(null);
  const [variantPick, setVariantPick] = useState<{ product: Product; qty: number } | null>(null);
  // New app-setting-driven bill context.
  const [counter, setCounter] = useState('1');
  const [premise, setPremise] = useState('');
  const [transport, setTransport] = useState<TransportInfo | null>(null);
  const [showTransport, setShowTransport] = useState(false);
  const [lastPaid, setLastPaid] = useState<Transaction | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showCombos, setShowCombos] = useState(false);
  const [tableSuffix, setTableSuffix] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const searchRef = useRef<HTMLInputElement>(null);

  // "F - Network Connection" — live online/offline badge.
  useEffect(() => {
    if (!networkIndicator) return;
    const up = () => setIsOnline(true);
    const down = () => setIsOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, [networkIndicator]);

  // The billing screen is a landscape 50/50 layout (bill_fragment.xml parity),
  // so force landscape while it's open and release the lock on leave. On the
  // native Android app this pins the Activity orientation, which rotates the
  // device even when system auto-rotate is OFF — otherwise the WebView ignores
  // the web lock, the CSS rotate(90deg) fallback kicks in, and touch-scrolling
  // the cart list inside that rotated element breaks. On the web it falls back
  // to the Screen Orientation API (and ultimately the CSS rotation).
  useEffect(() => lockLandscape(), []);

  // "C - Open Soft Keyboard" — focus the search box so the keyboard pops.
  useEffect(() => {
    if (openSoftKeyboard) searchRef.current?.focus();
  }, [openSoftKeyboard]);

  // "A - Opening Balance" — banner state for today's opening cash.
  const todayStr = new Date().toISOString().split('T')[0];
  const [openingSet, setOpeningSet] = useState(false);
  // "C - Enable Show Opening Amount": suggest yesterday's expected cash-in-hand.
  const [lastShiftCash, setLastShiftCash] = useState(0);
  useEffect(() => {
    let alive = true;
    Promise.all([db.getOpeningBalance(todayStr), db.getShifts()]).then(([ob, shifts]) => {
      if (!alive) return;
      setOpeningSet(ob > 0);
      setLastShiftCash(shifts.length ? shifts[shifts.length - 1].expectedCash : 0);
    });
    return () => {
      alive = false;
    };
  }, [todayStr]);
  const setOpening = () => {
    const suggestion = openingAmountShow && lastShiftCash > 0 ? lastShiftCash.toFixed(2) : '';
    const raw = window.prompt("Enter today's opening balance:", suggestion);
    if (raw == null) return;
    db.setOpeningBalance(todayStr, parseFloat(raw) || 0);
    setOpeningSet(true);
  };

  // Premises from the Service Point master (Premises Wise Tax / Show Premises First).
  const premises = useMemo(() => {
    const set = new Set<string>();
    servicePoints.forEach((sp) => sp.premise && set.add(sp.premise));
    return Array.from(set).sort();
  }, [servicePoints]);

  const taxRateFor = (p: Product) => p.taxRatePercent ?? config.taxRatePercent;
  const cur = config.currencySymbol;
  const money = (n: number) => `${cur} ${n.toFixed(2)}`;

  const lastBillTotal = useMemo(() => {
    const completed = sales.filter((s) => s.status === 'completed' && s.total > 0);
    return completed.length ? completed[completed.length - 1].total : 0;
  }, [sales]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.name.toLowerCase() === customerName.trim().toLowerCase()),
    [customers, customerName]
  );

  const categories = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => p.category && set.add(p.category));
    return ['All', ...Array.from(set).sort()];
  }, [products]);

  // Sub-categories of the active category: the Category Master children (in
  // serial order) plus any sub-category names already on products in this
  // category. Empty when "All" is selected or the category has no sub-cats.
  const subCategories = useMemo(() => {
    if (activeCategory === 'All') return [];
    const ordered = categoryMaster
      .filter((c) => c.parentCategory === activeCategory)
      .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name))
      .map((c) => c.name);
    const set = new Set(ordered);
    const extras: string[] = [];
    products.forEach((p) => {
      if (p.category === activeCategory && p.subCategory && !set.has(p.subCategory)) {
        set.add(p.subCategory);
        extras.push(p.subCategory);
      }
    });
    const all = [...ordered, ...extras.sort()];
    return all.length ? ['All', ...all] : [];
  }, [products, categoryMaster, activeCategory]);

  const visibleProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const code = search.trim();
    // "G - Three character search": name matching kicks in from 3 characters;
    // shorter terms only narrow the grid down when they exactly hit a
    // barcode / search-code / id (a scanner finishing a short code) — otherwise
    // the grid stays unfiltered rather than going blank while the cashier types.
    const nameSearchOk = !threeCharSearch || term.length >= 3;
    // A variant's own barcode / SKU counts as a code hit too, so a scanned
    // size/colour label narrows the grid to its parent product.
    const matchesCode = (p: Product) =>
      p.barcode === code ||
      p.searchCode === code ||
      String(p.id) === code ||
      (!!code &&
        !!p.variants?.some((v) => v.barcode === code || (v.sku && v.sku.toLowerCase() === term)));
    const codeHit = !nameSearchOk && !!code && products.some(matchesCode);
    const list = products.filter((p) => {
      const inCat = gridView === 'menu' || activeCategory === 'All' || p.category === activeCategory;
      if (!inCat) return false;
      // Drill-down: when a sub-category is picked, only its products show.
      // Picking the category itself ('All' sub-category) shows every product.
      if (gridView === 'category' && activeSubCategory !== 'All' && p.subCategory !== activeSubCategory) {
        return false;
      }
      if (!term) return true;
      if (!nameSearchOk) return !codeHit || matchesCode(p);
      return (
        p.name.toLowerCase().includes(term) ||
        p.regionalName?.toLowerCase().includes(term) ||
        p.barcode?.includes(search) ||
        matchesCode(p) ||
        String(p.id) === search ||
        // Acronym search: "VFR" matches "Veg Fried Rice" (first letter of each word).
        nameInitials(p.name).includes(term)
      );
    });
    // MENU CARD = hot-keys view: best sellers (keyCount) first, unless the
    // "Disable sorting in Hot keys" setting keeps the natural order.
    if (gridView === 'menu' && !hotKeysNoSort) {
      list.sort((a, b) => (b.keyCount ?? 0) - (a.keyCount ?? 0));
    }
    if (sortByName) list.sort((a, b) => a.name.localeCompare(b.name));
    // Typing "g" should surface products whose name starts with "g" before
    // ones that merely contain it elsewhere (e.g. "Green Tea" before "Mango").
    // Array.sort is stable, so each group keeps the ordering set above.
    if (term && nameSearchOk) {
      const startsWith = (p: Product) =>
        p.name.toLowerCase().startsWith(term) ||
        !!p.regionalName?.toLowerCase().startsWith(term) ||
        nameInitials(p.name).startsWith(term);
      list.sort((a, b) => Number(startsWith(b)) - Number(startsWith(a)));
    }
    return list;
  }, [products, search, activeCategory, activeSubCategory, gridView, hotKeysNoSort, sortByName, threeCharSearch]);

  // "A - Regional Language": show the product's regional name when present.
  const prodLabel = (p: Product) => (regionalNames && p.regionalName ? p.regionalName : p.name);
  // "C - Regional Language Category Name" from the category master.
  const catLabel = (c: string) => {
    if (!catRegional || c === 'All') return c;
    return categoryMaster.find((x) => x.name === c)?.regionalName || c;
  };

  const cessRateFor = (productId: string) => products.find((p) => String(p.id) === String(productId))?.cessPercent;

  // "Show on Bill" attributes (Attribute Master) rendered as one line of text
  // under the item name — e.g. "Size: Large · Colour: Red". Inactive attributes
  // and ones whose flag is off are skipped; master order (serial no) is kept.
  //
  // A variant line reports the variant's own value for a variant-axis attribute:
  // the product carries the whole axis (every size it comes in), which is not
  // what belongs on a bill. An axis the picked variant says nothing about is
  // left off rather than printed as a list of choices.
  const attributeTextFor = (productId: string, variant?: ProductVariant): string | undefined => {
    const p = products.find((x) => String(x.id) === String(productId));
    if (!p?.attributes?.length) return undefined;
    const parts = p.attributes
      .map((av) => ({ av, def: attributes.find((d) => d.id === av.attributeId) }))
      .filter(({ av, def }) => def && def.showOnBill && def.active !== false && av.values.length > 0)
      .sort((a, b) => (a.def!.serialNo ?? 9999) - (b.def!.serialNo ?? 9999))
      .map(({ av, def }) => {
        const fromVariant = variant?.attributeValues?.[def!.id];
        if (fromVariant) return `${def!.name}: ${fromVariant}`;
        if (def!.useForVariants) return '';
        return `${def!.name}: ${av.values.join(', ')}`;
      })
      .filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
  };

  // Per-line discount % (Android addProductToBill): with "Category Wise
  // Discount" on, the product's category master % wins, else the product's own
  // discount applies.
  const lineDiscountFor = (productId: string): number | undefined => {
    const p = products.find((x) => String(x.id) === String(productId));
    if (!p) return undefined;
    if (categoryDiscountOn && p.category) {
      const cat = categoryMaster.find((c) => c.name === p.category);
      if (cat?.discountPercent) return cat.discountPercent;
    }
    return p.discountPercent || undefined;
  };

  // "O - Allow Decimal Qty": without it, quantities are whole numbers. Weighable
  // / metered lines always keep their fractional amount regardless of the toggle.
  const clampQty = (n: number, weighable?: boolean) =>
    allowDecimalQty || weighable ? n : Math.trunc(n);
  // Keep the Qty text box from accepting characters the setting disallows
  // (a single decimal point only when decimals are permitted).
  const sanitizeQty = (raw: string, weighable?: boolean) => {
    if (!(allowDecimalQty || weighable)) return raw.replace(/[^0-9]/g, '');
    let v = raw.replace(/[^0-9.]/g, '');
    const dot = v.indexOf('.');
    if (dot >= 0) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
    return v;
  };

  const lineTotal = (qty: number, price: number, discountPercent?: number) =>
    +(qty * price * (1 - (discountPercent ?? 0) / 100)).toFixed(2);

  // Bill lines are identified by product *and* variant: two sizes of the same
  // shirt are two separate lines, and every per-line action (qty, price, edit,
  // delete, accumulate) has to address exactly one of them. Same key shape the
  // Goods Received screen uses for its receipt lines.
  const lineKey = (productId: string, variantId?: string) => `${productId}::${variantId ?? ''}`;
  const keyOfLine = (i: BillLine) => lineKey(i.productId, i.variantId);

  const addLine = (
    productId: string,
    name: string,
    qty: number,
    price: number,
    taxRatePercent: number,
    meta?: { weight?: number; bags?: number; unitName?: string },
    variant?: ProductVariant
  ) => {
    const cessPercent = cessRateFor(productId);
    const discountPercent = lineDiscountFor(productId);
    const attributeText = attributeTextFor(productId, variant);
    // "Salesman Product Wise" — stamp the currently-selected salesman per line.
    const lineSalesman = salesmanProductWise && salesman.trim() ? salesman.trim() : undefined;
    const key = lineKey(productId, variant?.id);
    setItems((prev) => {
      const existing = prev.find((i) => keyOfLine(i) === key);
      // "N - Add Product One Time": a product already on the bill is not added
      // (or accumulated) again — edit the existing line instead. A fully-sent
      // line is exempt: re-ordering the same dish must still add a new round.
      if (existing && addOneTime && !isLocked(existing)) return prev;
      // Weighable / metered lines are kept distinct (not accumulated) so each
      // weigh-in keeps its own weight & bag count.
      if (existing && !meta) {
        const q = existing.quantity + qty;
        return prev.map((i) =>
          keyOfLine(i) === key
            ? { ...i, quantity: q, unitPrice: price, total: lineTotal(q, price, i.discountPercent) }
            : i
        );
      }
      return [
        ...prev,
        {
          productId,
          name,
          quantity: qty,
          unitPrice: price,
          taxRatePercent,
          cessPercent,
          discountPercent,
          ...(attributeText ? { attributeText } : {}),
          ...(variant ? { variantId: variant.id } : {}),
          ...(lineSalesman ? { salesman: lineSalesman } : {}),
          total: lineTotal(qty, price, discountPercent),
          ...(meta?.weight != null ? { weight: meta.weight } : {}),
          ...(meta?.bags != null ? { bags: meta.bags } : {}),
          ...(meta?.unitName ? { unitName: meta.unitName } : {}),
        },
      ];
    });
  };

  // Wholesale mode uses Price 1 (priceTiers[0]) when set; else selling price.
  const priceFor = (p: Product) => {
    if (priceMode === 'wholesale' && showWholesale) {
      const wholesale = p.priceTiers?.[0];
      if (wholesale && wholesale > 0) return wholesale;
    }
    return p.price;
  };

  const addProduct = (p: Product, qty = 1) => addLine(p.id, p.name, qty, priceFor(p), taxRateFor(p));

  // True when this product should offer a variant chooser instead of being
  // added directly. Shared by grid taps, barcode scans and search.
  const hasVariants = (p: Product) => variantsOn && !!p.variants && p.variants.length > 0;

  // Products with variants open a chooser; otherwise a tap adds the product to
  // the bill immediately (no extra "+" click) and also drops it into the green
  // entry card already in "editing" mode, so Qty/Price/Total can still be
  // tweaked right after — same card editLine() uses for a line tapped later.
  const pickProduct = (p: Product) => {
    if (hasVariants(p)) {
      setVariantPick({ product: p, qty: 1 });
      setSearch('');
      return;
    }
    const existing = items.find((i) => i.productId === p.id);
    const accumulates = !existing || !(addOneTime && !isLocked(existing));
    const newQty = existing && accumulates ? existing.quantity + 1 : existing ? existing.quantity : 1;
    addProduct(p);
    setPending({
      productId: p.id,
      name: p.name,
      qty: String(newQty),
      price: String(priceFor(p)),
      taxRatePercent: taxRateFor(p),
      weighable: p.weighable,
      unitGroupId: p.unitGroupId,
      unitName: p.unit,
      basePrice: priceFor(p),
      editing: true,
    });
    setSearch('');
  };
  const chooseVariant = (p: Product, v: ProductVariant, qty = 1) => {
    addLine(p.id, `${p.name} · ${v.name}`, qty, v.price, taxRateFor(p), undefined, v);
    setVariantPick(null);
  };

  // Tapping a line already on the bill loads it back into the green entry card
  // so its Qty / Price / Total can be edited there. Sent (KOT'd) lines are
  // locked and ignore the tap.
  const editLine = (item: BillLine) => {
    if (isLocked(item)) return;
    setPending({
      productId: item.productId,
      variantId: item.variantId,
      name: item.name,
      qty: String(item.quantity),
      price: String(item.unitPrice),
      taxRatePercent: item.taxRatePercent,
      editing: true,
    });
  };

  const pendingTotal = pending ? (parseFloat(pending.qty) || 0) * (parseFloat(pending.price) || 0) : 0;

  const commitPending = () => {
    if (!pending) return;
    const q = clampQty(parseFloat(pending.qty) || 0, pending.weighable);
    const pr = parseFloat(pending.price) || 0;
    if (q <= 0) return;
    if (pending.editing) {
      // Replace the edited line's quantity/price outright.
      const key = lineKey(pending.productId, pending.variantId);
      setItems((prev) =>
        prev.map((i) =>
          keyOfLine(i) === key
            ? { ...i, quantity: q, unitPrice: pr, total: lineTotal(q, pr, i.discountPercent) }
            : i
        )
      );
    } else {
      const meta =
        pending.weighable || pending.unitName !== undefined
          ? {
              weight: pending.weighable ? q : undefined,
              bags: pending.bags ? parseFloat(pending.bags) || undefined : undefined,
              unitName: pending.unitName,
            }
          : undefined;
      const prod = products.find((x) => x.id === pending.productId);
      const variant = pending.variantId
        ? prod?.variants?.find((v) => v.id === pending.variantId)
        : undefined;
      addLine(pending.productId, pending.name, q, pr, pending.taxRatePercent, meta, variant);
    }
    setPending(null);
    setSearch('');
  };

  const changeQty = (key: string, qty: number) =>
    setItems((prev) =>
      prev
        .map((i) => {
          if (keyOfLine(i) !== key) return i;
          // Quantity can never drop below what was already fired to the kitchen.
          const q = Math.max(i.sentQty ?? 0, clampQty(qty, i.weight != null));
          return { ...i, quantity: q, total: lineTotal(q, i.unitPrice, i.discountPercent) };
        })
        .filter((i) => i.quantity > 0)
    );

  // Manual / Open Price (app_setting_layout.xml) — edit a line's unit price.
  // The price of an already-KOT'd line is frozen.
  const changePrice = (key: string, price: number) =>
    setItems((prev) =>
      prev.map((i) =>
        keyOfLine(i) === key && !isLocked(i)
          ? { ...i, unitPrice: price, total: lineTotal(i.quantity, price, i.discountPercent) }
          : i
      )
    );

  // A line that has been sent to the kitchen can't be removed from the bill —
  // cancel it through the KOT flow instead.
  const removeItem = (key: string) =>
    setItems((prev) => prev.filter((i) => !(keyOfLine(i) === key && !isLocked(i))));

  const clearBill = () => {
    setItems([]);
    setPending(null);
    setCustomerName('');
    setUdn('');
    setDiscAmt('');
    setDiscPct('');
    setCouponInput('');
    setAppliedCoupon('');
    setCouponMsg('');
    setDeliveryCharges('');
    setServicePct('');
    setServiceAmt('');
    setEstimate(false);
    setNonGstBill(null);
    setIgstBill(null);
    setExtraCharges('');
    setChallanNo('');
    setVehicleNo('');
    setRemark('');
    setOrderType('dinein');
    setTable('');
    setToken('');
    setCaptain('');
    setSalesman('');
    setKotRemark('');
    setKotSaved(false);
    setPremise('');
    setTransport(null);
    setTableSuffix('');
  };

  // Tables already occupied by held bills — surfaced in the table picker grid.
  const bookedTables = useMemo(
    () => new Set(heldBills.map((h) => h.table).filter(Boolean) as string[]),
    [heldBills]
  );

  // Table picker grid: the default 1–20 numbered tables plus any custom tables
  // configured on the Tables page or carrying a live running order, so a busy
  // table is always visible and colour-coded here.
  const pickerTables = useMemo(() => {
    const base = Array.from({ length: 20 }, (_, i) => String(i + 1));
    const extra = rest.tables.filter((t) => !base.includes(t));
    return [...base, ...extra];
  }, [rest.tables]);

  // Colour state of a table tile: a table carrying KOT'd items is "occupied",
  // one merely held / booked is "booked", otherwise "free".
  const tableStatus = (name: string): 'occupied' | 'booked' | 'free' => {
    const order = rest.runningOrder(name);
    if (order && order.items.length > 0) return 'occupied';
    if (order || bookedTables.has(name)) return 'booked';
    return 'free';
  };

  const pickTable = (t: string) => {
    const v = t.trim();
    if (!v) return;
    setTable(v);
    setTableInput('');
    setShowTablePicker(false);
    if (!enableTables) return;
    // Load the table's running order: items already fired to the kitchen come
    // back locked (sentQty = quantity) so they can't be re-edited or re-printed,
    // while new items can still be added and KOT'd on top.
    const order = rest.runningOrder(v);
    if (order) {
      setItems(order.items.map((i) => ({ ...i, sentQty: i.quantity })));
      setCaptain(order.waiter ?? '');
      setCustomerName(order.customerName ?? '');
      setKotSaved(true);
    } else {
      // Switching to a free table starts a clean order.
      setItems([]);
      setKotSaved(false);
    }
  };

  // Restaurant table flow (running order per table) is active only when the
  // Tables setting is on and a table is actually selected. Counter KOTs are not
  // attached to a running order, but every KOT is still incremental: only the
  // units not yet fired go out (a second "Print KOT" must never re-print the
  // first ticket's items). Re-Print KOT is the explicit full reprint.
  const tableMode = enableTables && !!table.trim();
  // How many line-units have not yet been fired to the kitchen.
  const unsentCount = items.reduce((s, i) => s + unsentOf(i), 0);
  const fireableCount = unsentCount;

  // Fire the not-yet-sent items to the kitchen as one KOT. In table mode the
  // ticket is appended to the table's running order (so the Tables/Captain
  // screens and the KOT report stay in sync) and the fired lines are then locked
  // so they can't be edited or re-printed; at the counter it is a one-off ticket
  // that leaves the bill editable. `print` also pushes it to the printer(s).
  const fireKot = async (print: boolean) => {
    const unsent = items.filter((i) => unsentOf(i) > 0);
    if (unsent.length === 0) return;
    const lineQty = (i: BillLine) => unsentOf(i);

    let kot: KotTicket | null;
    if (tableMode) {
      kot = await rest.sendKot(
        table.trim(),
        unsent.map((i) => ({
          productId: i.productId,
          name: i.attributeText ? `${i.name} (${i.attributeText})` : i.name,
          quantity: lineQty(i),
          unitPrice: i.unitPrice,
          remark: kotRemark.trim() || undefined,
        })),
        {
          waiter: captain.trim() || undefined,
          customerName: customerName.trim() || undefined,
          customerId: selectedCustomer?.id,
        }
      );
    } else {
      kot = {
        id: Date.now().toString(),
        kotNo: await db.nextKotNo(),
        tableName: table ? `Table ${table}` : 'COUNTER',
        waiter: captain.trim() || undefined,
        customerName: customerName.trim() || undefined,
        lines: unsent.map((i) => ({
          productId: i.productId,
          name: i.attributeText ? `${i.name} (${i.attributeText})` : i.name,
          quantity: lineQty(i),
          unitPrice: i.unitPrice,
          remark: kotRemark.trim() || undefined,
        })),
        amount: unsent.reduce((s, i) => s + lineQty(i) * i.unitPrice, 0),
        status: 'active',
        timestamp: new Date().toISOString(),
      };
    }
    // Lock everything now on the bill — it has all been fired to the kitchen,
    // so the next KOT only carries units added after this point.
    setItems((prev) => prev.map((i) => ({ ...i, sentQty: i.quantity })));

    setKotSaved(true);

    if (!print || !kot) return;
    // KOT prints over USB, routed to the station printers (falling back to a
    // formatted print window when no USB printer is reachable).
    const ticket = kot;
    printKotRouted(ticket, config.storeName, (pid) => products.find((p) => p.id === pid)?.servicePoint)
      .then((sentOverUsb) => {
        if (sentOverUsb) return;
        const w = window.open('', '_blank', 'width=360,height=600');
        if (!w) return;
        w.document.open();
        w.document.write(buildKotHtml(ticket, config.storeName));
        w.document.close();
      })
      .catch((e) => console.warn('KOT print failed:', e));
  };

  // "Save KOT" fires the new items silently; "Print KOT" fires and prints.
  const saveKot = () => fireKot(false);
  const printKot = () => fireKot(true);

  // Explicit "Re-Print KOT" (opt-in setting): reprint the already-fired items
  // without firing anything new and without changing the bill.
  const reprintKot = async () => {
    if (!items.length) return;
    const ticket: KotTicket = {
      id: Date.now().toString(),
      kotNo: await db.nextKotNo(),
      tableName: table ? `Table ${table}` : 'COUNTER',
      waiter: captain.trim() || undefined,
      customerName: customerName.trim() || undefined,
      lines: items.map((i) => ({
        productId: i.productId,
        name: i.attributeText ? `${i.name} (${i.attributeText})` : i.name,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        remark: kotRemark.trim() || undefined,
      })),
      amount: items.reduce((s, i) => s + i.total, 0),
      status: 'active',
      timestamp: new Date().toISOString(),
    };
    printKotRouted(ticket, config.storeName, (pid) => products.find((p) => p.id === pid)?.servicePoint)
      .then((sentOverUsb) => {
        if (sentOverUsb) return;
        const w = window.open('', '_blank', 'width=360,height=600');
        if (!w) return;
        w.document.open();
        w.document.write(buildKotHtml(ticket, config.storeName));
        w.document.close();
      })
      .catch((e) => console.warn('KOT reprint failed:', e));
  };

  const newBill = () => {
    if (items.length) holdBill();
    else clearBill();
  };

  // Resolve a scanned/typed code to a product, and to a specific variant when
  // the code is one the label printer issued for a size/colour row. Variant
  // barcodes and SKUs are matched FIRST: that is the whole point of printing a
  // label per variant — a scan at the till must land on the exact size and
  // colour, never on the parent product. Falls back to the product's own
  // barcode / search code / id. Mirrors the Goods Received resolver, so a
  // garment scanned in at receiving scans out at the counter the same way.
  const resolveCode = (raw: string): { product: Product; variant?: ProductVariant } | null => {
    const t = raw.trim();
    if (!t) return null;
    const lower = t.toLowerCase();
    if (variantsOn) {
      for (const p of products) {
        const variant = p.variants?.find(
          (v) => v.barcode === t || (v.sku && v.sku.toLowerCase() === lower)
        );
        if (variant) return { product: p, variant };
      }
    }
    const product = products.find(
      (p) => p.barcode === t || p.searchCode === t || String(p.id) === t
    );
    return product ? { product } : null;
  };

  // Weight-embedded barcode (Android: "w"/"W" + searchCode + 6-digit grams).
  // A weighable product is added with quantity = grams/1000; a non-weighable
  // one treats the 6 digits as a plain quantity. Returns true when handled.
  const tryWeightBarcode = (raw: string): boolean => {
    const code = raw.trim();
    if (!/^[wW]\d{6,}$/.test(code)) return false;
    const body = code.slice(1);
    const prefix = body.slice(0, -6);
    const grams = body.slice(-6);
    if (!prefix) return false;
    const product = products.find(
      (p) => p.searchCode === prefix || String(p.id) === prefix || p.searchCode === String(parseInt(prefix, 10))
    );
    if (!product) return false;
    if (product.weighable) {
      const weight = parseInt(grams, 10) / 1000;
      if (weight > 0) addLine(product.id, product.name, weight, priceFor(product), taxRateFor(product), { weight, unitName: product.unit });
    } else {
      const qty = parseInt(grams, 10);
      if (qty > 0) addLine(product.id, product.name, qty, priceFor(product), taxRateFor(product));
    }
    setSearch('');
    return true;
  };

  // Resolve a scanned or typed code to a bill action: weight-embedded barcode,
  // "qty*barcode", or a plain product match. Returns true when it was handled.
  // Shared by the search box (Enter) and the camera scanner.
  const addByCode = (raw: string): boolean => {
    const code = raw.trim();
    if (!code) return false;
    if (tryWeightBarcode(code)) return true;
    // "5 — Barcode Scanner / A - Search Barcode with Quantity": "3*<barcode>"
    // (or 3x<barcode>) adds 3 of the product in one scan.
    if (barcodeQty) {
      const m = code.match(/^(\d+(?:\.\d{1,3})?)\s*[*xX]\s*(\S+)$/);
      if (m) {
        const q = parseFloat(m[1]);
        const c = m[2];
        const hit = resolveCode(c);
        if (hit && q > 0) {
          const { product: prod, variant } = hit;
          // A variant barcode already names the size/colour — no chooser.
          if (variant) chooseVariant(prod, variant, q);
          else if (hasVariants(prod)) setVariantPick({ product: prod, qty: q });
          else addLine(prod.id, prod.name, q, priceFor(prod), taxRateFor(prod));
          setSearch('');
          return true;
        }
      }
    }
    const hit = resolveCode(code);
    if (hit) {
      if (hit.variant) {
        // Scanned a variant label: add that exact size/colour straight away.
        chooseVariant(hit.product, hit.variant);
        setSearch('');
      } else {
        // Same add-to-bill behavior as tapping the product tile.
        pickProduct(hit.product);
      }
      return true;
    }
    return false;
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !search.trim()) return;
    if (addByCode(search)) return;
    // No exact barcode/code match (e.g. a name search) — fall back to the
    // first product still visible in the filtered grid, same as tapping it.
    const first = visibleProducts[0];
    if (!first) return;
    const blocked = first.stock <= 0 && first.stockable !== false && !stockMinus;
    if (blocked) return;
    // Same add-to-bill behavior as tapping the product tile.
    pickProduct(first);
  };

  // Camera scan result: act on it like a scanned barcode; if nothing matched,
  // drop the value into the search box so the user can refine it.
  const handleScan = (code: string) => {
    setShowScanner(false);
    if (!addByCode(code)) setSearch(code.trim());
    searchRef.current?.focus();
  };

  // "Barcode Scan Directly" — a 13-char weight barcode is consumed as soon as
  // it lands in the box (scanners that don't send Enter).
  const onSearchChange = (value: string) => {
    if (barcodeScanDirectly && /^[wW]\d{12}$/.test(value.trim())) {
      if (tryWeightBarcode(value)) return;
    }
    setSearch(value);
  };

  // A non-GST outlet or an estimate bill carries no GST/CESS breakup. The
  // per-bill checkboxes (Android Non-GST / IGST print buttons) override the
  // global app-setting default for this bill only.
  const effNonGst = nonGstBill ?? nonGstMode;
  const effIgst = igstBill ?? igstMode;
  const gstFlag = !effNonGst && !estimate;

  // Bill-amount discount slabs (Android DiscountRepository): the matching
  // slab's % applies automatically when no manual discount/coupon was entered.
  const [discountSlabs, setDiscountSlabs] = useState<DiscountSlab[]>([]);
  useEffect(() => {
    db.getDiscountSlabs().then(setDiscountSlabs);
  }, []);
  const itemsAmount = items.reduce((s, i) => s + i.total, 0);
  const slabPct =
    !discAmt && !discPct && !appliedCoupon
      ? db.discountPercentageFor(itemsAmount, discountSlabs)
      : 0;

  const totals = useMemo(() => {
    const t = computeBillTotals({
      lines: items.map((i) => ({ total: i.total, taxRatePercent: i.taxRatePercent, cessPercent: i.cessPercent })),
      discountAmount: parseFloat(discAmt) || 0,
      discountPercent: parseFloat(discPct) || slabPct,
      serviceChargeAmount: parseFloat(serviceAmt) || 0,
      serviceChargePercent: parseFloat(servicePct) || 0,
      parcelCharges: parseFloat(deliveryCharges) || 0,
      extraCharges: parseFloat(extraCharges) || 0,
      taxInclusive,
      gstFlag,
      igst: effIgst,
      roundOffEnabled: roundOffOn,
    });
    // Keep the existing field names (billAmt/discount/tax/delivery/roundOff/finalTotal)
    // that the rest of this component reads, plus the new charge fields.
    return { ...t, delivery: t.parcel };
  }, [items, discAmt, discPct, slabPct, serviceAmt, servicePct, deliveryCharges, extraCharges, taxInclusive, gstFlag, effIgst, roundOffOn]);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);

  // Feed the Customer Display (/led-display): broadcast the in-progress bill,
  // the just-settled bill (thank-you state), or idle.
  useEffect(() => {
    publishLedSnapshot({
      state: items.length ? 'billing' : lastPaid ? 'paid' : 'idle',
      storeName: config.storeName,
      currency: cur,
      lines: items.map((i) => ({ name: i.name, quantity: i.quantity, unitPrice: i.unitPrice, total: i.total })),
      totalQty,
      billAmt: totals.billAmt,
      discount: totals.discount,
      tax: totals.tax,
      finalTotal: totals.finalTotal,
      paidBillNo: !items.length && lastPaid ? lastPaid.billNo : undefined,
      paidTotal: !items.length && lastPaid ? lastPaid.total : undefined,
    });
  }, [items, totals, totalQty, lastPaid, config.storeName, cur]);

  // Validate + apply a coupon code against the current bill amount. A percent
  // coupon fills the Disc% field, a flat coupon fills the Disc ₹ field. With
  // "Multi Coupon" on (Android ButtonGrid 29), several coupons stack — their
  // percents / amounts accumulate and the codes join up on the bill.
  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    const applied = appliedCoupon ? appliedCoupon.split(',') : [];
    if (applied.length > 0 && !multiCouponOn) return setCouponMsg('A coupon is already applied');
    if (applied.includes(code)) return setCouponMsg('Coupon already applied');
    const c = coupons.find((x) => x.code.toUpperCase() === code);
    if (!c) return setCouponMsg('Invalid coupon code');
    if (!c.active) return setCouponMsg('Coupon is inactive');
    if (c.expiry && new Date(c.expiry + 'T23:59:59') < new Date()) return setCouponMsg('Coupon has expired');
    if (c.minBill && totals.billAmt < c.minBill) return setCouponMsg(`Min bill ${config.currencySymbol}${c.minBill} required`);
    if (c.type === 'percent') {
      setDiscPct((prev) => String((parseFloat(prev) || 0) + c.value));
    } else {
      setDiscAmt((prev) => String((parseFloat(prev) || 0) + c.value));
    }
    const codes = [...applied, c.code];
    setAppliedCoupon(codes.join(','));
    setCouponInput('');
    setCouponMsg(`Applied ${codes.join(', ')}`);
  };

  const clearCoupon = () => {
    setAppliedCoupon('');
    setCouponInput('');
    setCouponMsg('');
    setDiscPct('');
    setDiscAmt('');
  };

  // Shared by holdBill and recallBill (the latter parks the in-progress bill
  // back onto the list before swapping in the recalled one).
  const parkCurrentBill = (): HeldBill => {
    const who = table ? `T${table}` : customerName || 'Bill';
    const qty = `${items.length} item${items.length === 1 ? '' : 's'}`;
    return {
      id: Date.now().toString(),
      label: `${who} · ${qty} · ${money(totals.finalTotal)}`,
      items,
      customerName,
      table: table || undefined,
      heldAt: Date.now(),
    };
  };

  const holdBill = () => {
    if (!items.length) return;
    // Android caps the hold list at 5 bills (HoldBillListRemove5).
    if (heldBills.length >= 5) {
      alert('Hold list is full (max 5 bills). Recall or settle a held bill first.');
      return;
    }
    setHeldBills((prev) => [...prev, parkCurrentBill()]);
    clearBill();
  };

  const recallBill = (h: HeldBill) => {
    // Swap, don't discard: if a bill is already in progress, park it back
    // onto the list (bypassing the cap — this is a like-for-like swap, not
    // growth) so tapping the wrong chip never loses the operator's work.
    setHeldBills((prev) => {
      const rest = prev.filter((x) => x.id !== h.id);
      return items.length ? [...rest, parkCurrentBill()] : rest;
    });
    setItems(h.items);
    setCustomerName(h.customerName);
    setTable(h.table ?? '');
  };

  // Reason the bill can't be checked out yet, given Table/KOT settings (or null).
  const checkoutBlock = useMemo(() => {
    if (enableTables && tableForcefully && !table.trim()) return 'Select a table first';
    if (kotEnabled && noBillWithoutKot && !kotSaved) return 'Save KOT first';
    // Proper table flow: new items must be fired to the kitchen before billing.
    if (enableTables && table.trim() && kotEnabled && unsentCount > 0) return 'Send KOT for new items first';
    if (enableSalesman && salesmanForce && !salesman.trim()) return 'Select a salesman first';
    // "C - Customer Forcefully (Bill)" — a customer must be linked to bill.
    if (customerForce && !customerName.trim()) return 'Add a customer first';
    return null;
  }, [enableTables, tableForcefully, table, kotEnabled, noBillWithoutKot, kotSaved, unsentCount, enableSalesman, salesmanForce, salesman, customerForce, customerName]);

  const buildDraftTx = async (): Promise<Transaction> => {
    const pm: Transaction['paymentMethod'] = payMode === 'Cash' ? 'cash' : payMode === 'Digital' ? 'digital' : 'card';
    const operator = await db.getCurrentUser();
    return {
      id: Date.now().toString(),
      // Preview number only; recordSale assigns the committed sequential number.
      billNo: await db.peekNextBillNo(),
      customerId: selectedCustomer?.id,
      customerName: customerName.trim() || selectedCustomer?.name || undefined,
      udn: udn.trim() || undefined,
      table: table.trim() || undefined,
      token: token.trim() || undefined,
      counter: counterOn ? counter : undefined,
      premise: premise || undefined,
      transport: transportOn && transport?.vehicleNo?.trim() ? transport : undefined,
      captain: captain.trim() || undefined,
      salesman: salesman.trim() || undefined,
      kotRemark: kotRemark.trim() || undefined,
      orderType,
      priceMode,
      // Drop the transient KOT-tracking field before the line is persisted.
      items: items.map(({ sentQty, ...line }) => line),
      subtotal: totals.billAmt,
      tax: totals.tax,
      cess: totals.cess || undefined,
      serviceCharge: totals.serviceCharge || undefined,
      serviceChargePercent: parseFloat(servicePct) || undefined,
      discount: totals.discount,
      parcelCharges: totals.delivery,
      extraCharges: totals.extraCharges || undefined,
      couponCode: appliedCoupon || undefined,
      roundOff: totals.roundOff,
      total: totals.finalTotal,
      estimate: estimate || undefined,
      gstFlag,
      deliveryChallanNo: challanNo.trim() || undefined,
      vehicleNo: vehicleNo.trim() || transport?.vehicleNo?.trim() || undefined,
      userId: operator?.id,
      userName: operator?.name,
      paymentMethod: pm,
      timestamp: new Date().toISOString(),
      status: 'pending',
    };
  };

  const checkout = async () => {
    if (!items.length) return;
    if (checkoutBlock) return;
    setPendingTx(await buildDraftTx());
    setShowPayment(true);
  };

  // "Keyboard Setting / A - Connect Keyboard": physical-keyboard shortcuts —
  // F2 focuses search, F4 opens payment, Esc drops the pending entry.
  // (Re-subscribed every render so the handlers see fresh state.)
  useEffect(() => {
    if (!connectKeyboard) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'F4') {
        e.preventDefault();
        checkout();
      } else if (e.key === 'Escape') {
        setPending(null);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  });

  // "B - Floating Keyboard Button" — keypad types into the pending Qty (or the
  // search box when nothing is pending).
  const keypadPress = (k: string) => {
    if (k === '↵') {
      if (pending) commitPending();
      setShowKeypad(false);
      return;
    }
    const apply = (v: string) => (k === '⌫' ? v.slice(0, -1) : v + k);
    if (pending) setPending({ ...pending, qty: sanitizeQty(apply(pending.qty), pending.weighable) });
    else setSearch((s) => apply(s));
  };

  // Once a table's bill is recorded, mark its running order settled and free the
  // table (its KOTs become 'billed'). No-op outside table mode / empty tables.
  const finalizeTableIfAny = (finalized: Transaction) => {
    if (!enableTables || !table.trim()) return;
    if (rest.runningOrder(table.trim())) rest.finalizeSettle(table.trim(), finalized);
  };

  // Bill Settlement (Android ButtonGrid 18): save the bill unsettled (status
  // 'pending', stock already consumed) — it's settled later from Edit Bill.
  const saveUnsettled = async () => {
    if (!items.length || checkoutBlock) return;
    const finalized = await recordSale(await buildDraftTx());
    finalizeTableIfAny(finalized);
    printReceiptAuto(finalized, config).catch((e) => console.warn('Print failed:', e));
    maybePrintCoupon(finalized, config, 'print');
    clearBill();
  };

  // Save Bill / Print Bill (Android bill screen buttons): record the sale
  // immediately as completed with the selected payment mode — no payment
  // dialog. Print Bill also prints the receipt.
  const saveBill = async (print: boolean) => {
    if (!items.length || checkoutBlock) return;
    const finalized = await recordSale({ ...(await buildDraftTx()), status: 'completed' });
    finalizeTableIfAny(finalized);
    if (print) printReceiptAuto(finalized, config).catch((e) => console.warn('Print failed:', e));
    maybePrintCoupon(finalized, config, print ? 'print' : 'save');
    clearBill();
  };

  // Customer advance from the POS screen (Android AdvanceFunction): records an
  // advance CashFlow entry against the linked customer.
  const takeAdvance = () => {
    if (!selectedCustomer) return;
    const raw = window.prompt(`Advance amount from ${selectedCustomer.name}:`, '');
    if (raw == null) return;
    const amount = parseFloat(raw) || 0;
    if (amount <= 0) return;
    addCashFlow({
      type: 'advance',
      amount,
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.name,
      paymentMethod: 'cash',
      note: 'Advance (POS)',
    });
    alert(`Advance of ${money(amount)} recorded for ${selectedCustomer.name}.`);
  };

  const onPaid = async (tx: Transaction): Promise<Transaction> => {
    const finalized = await recordSale(tx);
    finalizeTableIfAny(finalized);
    setShowPayment(false);
    setPendingTx(null);
    clearBill();
    // "3. Show Print Buttons After Pay" (+ email / A4 delivery note actions):
    // keep the finalized bill around for the post-pay action dialog.
    if (
      showPrintAfterPay ||
      emailBillOn ||
      (a4Note && (finalized.deliveryChallanNo || finalized.vehicleNo))
    ) {
      setLastPaid(finalized);
    }
    return finalized;
  };

  return (
    <div className="bill2">
      {/* ============ LEFT PANE ============ */}
      <div className="bill2-left">
        {/* "A - Opening Balance": prompt for today's opening cash */}
        {openingBalanceOn && !openingSet && (
          <div className="b2-opening-banner">
            <span>Opening balance for today is not set.</span>
            <button onClick={setOpening}>Set Opening Balance</button>
          </div>
        )}

        {/* status badges (Network Connection / Store / Day-Night) */}
        {(networkIndicator || storeOn || dayNightOn) && (
          <div className="b2-badges">
            {networkIndicator && (
              <span className={`b2-badge ${isOnline ? 'ok' : 'bad'}`}>
                {isOnline ? '● Online' : '● Offline'}
              </span>
            )}
            {storeOn && <span className="b2-badge store">🏬 {config.storeName}</span>}
            {dayNightOn && (
              <span className="b2-badge">
                {dayNightOf(new Date().toISOString()) === 'DAY' ? '☀ DAY' : '☾ NIGHT'}
              </span>
            )}
          </div>
        )}

        {/* customer + UDN row (layoutAddTable) */}
        <div className="b2-toprow">
          {enableTables && (
            <div
              className={`b2-table-box ${table ? tableStatus(table) : ''}`}
              onClick={() => setShowTablePicker(true)}
            >
              {table ? (
                <>
                  <span className="b2-table-val">Table {table}</span>
                  <span
                    className="b2-box-x"
                    title="Leave this table"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Detach from the table; its running order stays saved.
                      setTable('');
                      setItems([]);
                      setKotSaved(false);
                    }}
                  >
                    ✕
                  </span>
                </>
              ) : (
                <span className="b2-table-add">Add Table +</span>
              )}
            </div>
          )}
          {askCaptain && (
            <div className="b2-cust-box">
              <input placeholder="Captain" value={captain} onChange={(e) => setCaptain(e.target.value)} />
              {captain && (
                <span className="b2-box-x" onClick={() => setCaptain('')}>
                  ✕
                </span>
              )}
            </div>
          )}
          {enableSalesman && (
            <div className="b2-cust-box">
              <select
                className="b2-cust-open"
                value={salesman}
                onChange={(e) => setSalesman(e.target.value)}
              >
                <option value="">{salesmanForce ? 'Salesman *' : 'Salesman'}</option>
                {salesmen.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          {showCustomer && (
            <div className="b2-cust-box">
              <button
                type="button"
                className="b2-cust-open"
                onClick={() => setShowCustPicker(true)}
              >
                {customerName || 'Add Customer +'}
              </button>
              {customerName && (
                <span className="b2-box-x" onClick={() => setCustomerName('')}>
                  ✕
                </span>
              )}
            </div>
          )}
          {udnOn && (
            <div className="b2-udn-box">
              <input placeholder="UDN" value={udn} onChange={(e) => setUdn(e.target.value)} />
              {udn && (
                <span className="b2-box-x" onClick={() => setUdn('')}>
                  ✕
                </span>
              )}
            </div>
          )}
          {/* "F - Transport Details" (transport_details.xml) */}
          {transportOn && (
            <div className="b2-cust-box">
              <button type="button" className="b2-cust-open" onClick={() => setShowTransport(true)}>
                {transport?.vehicleNo ? `🚚 ${transport.vehicleNo}` : 'Transport +'}
              </button>
              {transport && (
                <span className="b2-box-x" onClick={() => setTransport(null)}>
                  ✕
                </span>
              )}
            </div>
          )}
        </div>

        {/* search */}
        {!hideSearchBox && (
          <div className="b2-search card-rounded">
            <input
              ref={searchRef}
              className="b2-search-input"
              placeholder={barcodeQty ? 'Search by Code/Barcode/Name (qty*barcode)' : 'Search by Code/Barcode/Name'}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={onSearchKey}
            />
            <button
              type="button"
              className="b2-search-btn b2-scan-btn"
              onClick={() => setShowScanner(true)}
              title="Scan barcode with camera"
            >
              📷
            </button>
            <div className="b2-search-btn">🔍</div>
          </div>
        )}

        {/* green entry card: Amt | Qty | Price | Total. The Amt box (typing an
            amount back-calculates Qty) is the "1. Calculate reverse quantity"
            feature, so it only shows when that setting is on. */}
        <div className="b2-entry card-green">
          {reverseQty && (
            <input
              className="b2-ebox"
              placeholder="Amt"
              value={pending && pendingTotal ? pendingTotal.toFixed(2) : ''}
              onChange={(e) => {
                if (!pending) return;
                const amt = parseFloat(e.target.value) || 0;
                const pr = parseFloat(pending.price) || 0;
                setPending({ ...pending, qty: pr ? String(clampQty(+(amt / pr).toFixed(3), pending.weighable)) : pending.qty });
              }}
            />
          )}
          <input
            className="b2-ebox"
            placeholder="Qty"
            inputMode={allowDecimalQty || pending?.weighable ? 'decimal' : 'numeric'}
            value={pending?.qty ?? ''}
            onChange={(e) => pending && setPending({ ...pending, qty: sanitizeQty(e.target.value, pending.weighable) })}
            onKeyDown={(e) => e.key === 'Enter' && commitPending()}
          />
          <input
            className="b2-ebox"
            placeholder="Price"
            value={pending?.price ?? ''}
            onChange={(e) => pending && setPending({ ...pending, price: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && commitPending()}
          />
          {/* edt_product_total — editable: typing a total back-calculates Qty */}
          <input
            className="b2-ebox total"
            placeholder="Total"
            value={pending && pendingTotal ? pendingTotal.toFixed(2) : ''}
            onChange={(e) => {
              if (!pending) return;
              const tot = parseFloat(e.target.value) || 0;
              const pr = parseFloat(pending.price) || 0;
              setPending({ ...pending, qty: pr ? String(clampQty(+(tot / pr).toFixed(3), pending.weighable)) : pending.qty });
            }}
            onKeyDown={(e) => e.key === 'Enter' && commitPending()}
          />
          <button
            className={`b2-ebox-add ${pending?.editing ? 'editing' : ''}`}
            onClick={commitPending}
            disabled={!pending}
            title={pending?.editing ? 'Update line' : 'Add to bill'}
          >
            {pending?.editing ? '✓' : '＋'}
          </button>
        </div>

        {/* weighable / multi-unit capture row (shown only for such products) */}
        {pending && (pending.weighable || pending.unitGroupId) && (
          <div className="b2-uom">
            {pending.unitGroupId && (() => {
              const grp = unitGroups.find((g) => g.id === pending.unitGroupId);
              if (!grp) return null;
              return (
                <label>
                  Unit
                  <select
                    value={pending.unitName ?? ''}
                    onChange={(e) => {
                      const u = grp.units.find((x) => x.name === e.target.value);
                      const base = pending.basePrice ?? (parseFloat(pending.price) || 0);
                      setPending({
                        ...pending,
                        unitName: e.target.value,
                        price: String(+(base * (u?.factor ?? 1)).toFixed(2)),
                      });
                    }}
                  >
                    {grp.units.map((u) => (
                      <option key={u.name} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })()}
            {pending.weighable && (
              <label>
                Bags
                <input
                  type="number"
                  placeholder="0"
                  value={pending.bags ?? ''}
                  onChange={(e) => setPending({ ...pending, bags: e.target.value })}
                />
              </label>
            )}
          </div>
        )}

        {/* dine-in/parcel + retail/wholesale (orange-bordered rows) */}
        {(showDineInParcel || showWholesale || selfServiceOn) && (
          <div className="b2-leftseg card-orange">
            {showDineInParcel && (
              <>
                <button className={orderType === 'dinein' ? 'on' : ''} onClick={() => setOrderType('dinein')}>
                  Dine-in
                </button>
                <button className={orderType === 'parcel' ? 'on' : ''} onClick={() => setOrderType('parcel')}>
                  Parcel
                </button>
              </>
            )}
            {/* "14 — Bill Settlement / C - Self Services" order type */}
            {selfServiceOn && (
              <button className={orderType === 'self' ? 'on' : ''} onClick={() => setOrderType('self')}>
                Self
              </button>
            )}
            {(showDineInParcel || selfServiceOn) && showWholesale && <span className="b2-seg-sep" />}
            {showWholesale && (
              <>
                <button className={priceMode === 'retail' ? 'on' : ''} onClick={() => setPriceMode('retail')}>
                  Retail
                </button>
                <button className={priceMode === 'wholesale' ? 'on' : ''} onClick={() => setPriceMode('wholesale')}>
                  Wholesale
                </button>
              </>
            )}
          </div>
        )}

        {/* "Counter 1 & 2" — which till the bill is rung up on */}
        {counterOn && (
          <div className="b2-leftseg card-orange">
            <button className={counter === '1' ? 'on' : ''} onClick={() => setCounter('1')}>
              Counter 1
            </button>
            <button className={counter === '2' ? 'on' : ''} onClick={() => setCounter('2')}>
              Counter 2
            </button>
          </div>
        )}

        {/* KOT remark (16 — KOT Setting / 2 - KOT Remark) */}
        {kotRemarkOn && (
          <div className="b2-kot-remark card-orange">
            <span className="b2-kot-remark-lbl">KOT REMARK :</span>
            <input
              placeholder="e.g. Less spicy, No onion"
              value={kotRemark}
              onChange={(e) => setKotRemark(e.target.value)}
            />
          </div>
        )}

        {pending && (
          <div className="b2-pending">
            {pending.editing ? 'Editing' : 'Adding'}: <b>{pending.name}</b> — edit Qty / Price / Total, press Enter or
            {pending.editing ? ' ✓ to update' : ' ＋ to add'}
          </div>
        )}

        {/* item list */}
        <div className="b2-items">
          {items.length === 0 ? (
            <div className="b2-empty">Search or tap a product to add items</div>
          ) : (
            items.map((i) => {
              const locked = isLocked(i);
              return (
              <div className={`b2-item ${locked ? 'sent' : ''}`} key={i.productId}>
                {locked ? (
                  <span className="b2-sent-lock" title="Sent to kitchen — locked">🔒</span>
                ) : (
                  <span className="b2-del" onClick={() => removeItem(keyOfLine(i))}>
                    ✕
                  </span>
                )}
                <div className="b2-item-body">
                  <div
                    className={`b2-item-name ${locked ? '' : 'editable'} ${pending?.editing && pending.productId === i.productId ? 'active' : ''}`}
                    onClick={() => editLine(i)}
                    title={locked ? 'Already sent to the kitchen' : 'Edit Qty / Price in the entry box above'}
                  >
                    {i.name}
                    {locked && <span className="b2-sent-tag">SENT</span>}
                    {locked && unsentOf(i) > 0 && <span className="b2-new-tag">+{unsentOf(i)} new</span>}
                    {i.attributeText && <span className="b2-item-attrs">{i.attributeText}</span>}
                  </div>
                  <div className="b2-item-calc">
                    <span className="b2-qtybox">
                      <button onClick={() => changeQty(keyOfLine(i), i.quantity - 1)} disabled={i.quantity <= (i.sentQty ?? 0)}>−</button>
                      <input
                        inputMode={allowDecimalQty || i.weight != null ? 'decimal' : 'numeric'}
                        value={i.quantity}
                        onChange={(e) => changeQty(keyOfLine(i), parseFloat(sanitizeQty(e.target.value, i.weight != null)) || 0)}
                      />
                      <button onClick={() => changeQty(keyOfLine(i), i.quantity + 1)}>+</button>
                    </span>
                    <span className="b2-orange">{i.quantity}</span>
                    <span className="b2-x">X</span>
                    {allowManualPrice && !locked ? (
                      <input
                        className="b2-priceedit"
                        value={i.unitPrice}
                        onChange={(e) => changePrice(keyOfLine(i), parseFloat(e.target.value) || 0)}
                      />
                    ) : (
                      <span className="b2-orange">{i.unitPrice.toFixed(2)}</span>
                    )}
                    {i.discountPercent ? (
                      <span className="b2-orange" title="Line discount (category/product)">−{i.discountPercent}%</span>
                    ) : null}
                    {/* "M - Disable Product Total Amt" hides the line total */}
                    {!hideLineTotal && (
                      <>
                        <span className="b2-x">=</span>
                        <span className="b2-linetotal">{i.total.toFixed(2)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>

        {/* KOT action bar (16 — KOT Setting) */}
        {kotEnabled && (
          <div className="b2-kotbar">
            {showSaveKot && (
              <button
                className={`b2-kot-btn save ${kotSaved && !unsentCount ? 'done' : ''}`}
                onClick={saveKot}
                disabled={!fireableCount}
                title={!fireableCount ? 'No new items to send' : 'Send new items to the kitchen'}
              >
                {kotSaved && !unsentCount ? '✓ KOT Saved' : kotSaved ? 'Send New KOT' : 'Save KOT'}
              </button>
            )}
            {showPrintKot && (
              <button
                className="b2-kot-btn print"
                onClick={printKot}
                disabled={!fireableCount}
                title={!fireableCount ? 'Already sent — nothing new to print' : 'Print the items'}
              >
                Print KOT
              </button>
            )}
            {kotReprint && (
              <button
                className="b2-kot-btn reprint"
                onClick={reprintKot}
                disabled={!kotSaved || unsentCount > 0}
              >
                Re-Print KOT
              </button>
            )}
          </div>
        )}

        {/* Save / Print Bill row — its own bar above the NET AMT section */}
        {(!disableSaveBill || !disablePrintBill) && (
          <div className="b2-billbar">
            {!disableSaveBill && (
              <button
                className="b2-kot-btn save"
                onClick={() => saveBill(false)}
                disabled={!items.length || !!checkoutBlock}
                title={checkoutBlock ?? 'Save the bill without printing'}
              >
                Save Bill
              </button>
            )}
            {!disablePrintBill && (
              <button
                className="b2-kot-btn print"
                onClick={() => saveBill(true)}
                disabled={!items.length || !!checkoutBlock}
                title={checkoutBlock ?? 'Save and print the bill'}
              >
                Print Bill
              </button>
            )}
          </div>
        )}

        {/* bottom: Last Bill + NET AMT (layout_bottom) */}
        <div className="b2-bottombar">
          <div className="b2-lastbill">Last Bill : {lastBillTotal.toFixed(2)}</div>
          {billSettlementOn && (
            <button
              className="b2-kot-btn save"
              onClick={saveUnsettled}
              disabled={!items.length || !!checkoutBlock}
              title="Save unsettled — collect payment later from Edit Bill"
            >
              Settle Later
            </button>
          )}
          <button
            className="b2-netbar"
            onClick={checkout}
            disabled={!items.length || !!checkoutBlock}
            title={checkoutBlock ?? undefined}
          >
            <span className="b2-net-counts">
              <span>Items : {items.length}</span>
              <span>Qty : {totalQty}</span>
            </span>
            <span className="b2-net-amt">
              {checkoutBlock ? checkoutBlock : `NET AMT : ${totals.finalTotal.toFixed(2)} →`}
            </span>
          </button>
        </div>
      </div>

      {/* ============ RIGHT PANE ============ */}
      <div className="bill2-right">
        {/* action bar */}
        <div className="b2-actionbar card-rounded">
          {showHoldList ? (
            <>
              <button className="b2-act-add" onClick={newBill} title="New bill (holds current)">
                ＋
              </button>
              <div className="b2-held">
                {heldBills.length === 0 ? (
                  <span className="b2-held-empty">No held bills</span>
                ) : (
                  heldBills.map((h) => (
                    <button
                      key={h.id}
                      className="b2-held-chip"
                      onClick={() => recallBill(h)}
                      title={items.length ? 'Recall (swaps with the bill on screen)' : 'Recall this bill'}
                    >
                      {h.label}
                      <span className="b2-held-chip-time">{agoLabel(h.heldAt)}</span>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <button className="b2-act-add" onClick={clearBill} title="New bill">
              ＋
            </button>
          )}
          <button
            className="b2-menucard"
            onClick={() => setGridView((v) => (v === 'menu' ? 'category' : 'menu'))}
            title={gridView === 'menu' ? 'Switch to category view' : 'Switch to best-seller (hot keys) view'}
          >
            {gridView === 'menu' ? 'CATEGORY' : 'MENU CARD'}
          </button>
          <button className="b2-close" onClick={clearBill} title="Clear bill">
            ✕
          </button>
        </div>

        {/* premise chips ("G - Show Premises First" / "C - Premises Wise Tax") */}
        {(showPremisesFirst || premisesTax) && premises.length > 0 && (
          <div className="b2-cats b2-premises">
            <span className="b2-prem-lbl">PREMISE :</span>
            {premises.map((pr) => (
              <button
                key={pr}
                className={`b2-cat ${premise === pr ? 'active' : ''}`}
                onClick={() => setPremise(premise === pr ? '' : pr)}
              >
                {pr}
              </button>
            ))}
          </div>
        )}

        {/* token number (15 — Table Settings / J - Enter Token Number) */}
        {enterToken && (
          <div className="b2-token-row">
            <input
              className="b2-token-input"
              placeholder="ENTER TOKEN"
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </div>
        )}

        {/* category + product buttons (hidden in MENU CARD / hot-keys view).
            "A - GridView Style" wraps the chips into an N-column grid;
            "B - Category View Vertical" puts the strip beside the products. */}
        <div className={`b2-prodzone ${catVertical ? 'vertical' : ''} ${showBillDetails ? '' : 'fill'}`}>
          {gridView === 'category' && (
            <div className={`b2-cats ${catGrid ? `gridview cols-${catCols}` : ''} ${catVertical ? 'vertical' : ''}`}>
              {combosInBilling && recipes.length > 0 && (
                <button className="b2-cat combo" onClick={() => setShowCombos(true)}>
                  🍱 COMBO
                </button>
              )}
              {categories.map((c) => (
                <button key={c} className={`b2-cat ${activeCategory === c ? 'active' : ''}`} onClick={() => selectCategory(c)}>
                  {catLabel(c)}
                </button>
              ))}
            </div>
          )}
          {/* Sub-category strip: appears once a category with sub-categories is
              selected. "All" shows the whole category; a sub-category drills in. */}
          {gridView === 'category' && subCategories.length > 0 && (
            <div className={`b2-cats b2-subcats ${catGrid ? `gridview cols-${catCols}` : ''} ${catVertical ? 'vertical' : ''}`}>
              {subCategories.map((s) => (
                <button
                  key={s}
                  className={`b2-cat b2-subcat ${activeSubCategory === s ? 'active' : ''}`}
                  onClick={() => setActiveSubCategory(s)}
                >
                  {s === 'All' ? `All ${catLabel(activeCategory)}` : catLabel(s)}
                </button>
              ))}
            </div>
          )}
          <div
            key={loadAnim ? `${gridView}-${activeCategory}-${activeSubCategory}` : 'grid'}
            className={`b2-products style-${listStyle.toLowerCase()} ${hideProductList ? 'hidden' : ''} ${loadAnim ? 'anim' : ''}`}
          >
            {hideProductList ? null : visibleProducts.length === 0 ? (
              <div className="b2-empty">No products. Add some in Product Master.</div>
            ) : (
              visibleProducts.map((p) => {
                // Only stockable products block at zero stock, and "Stock Getting
                // in Minus" lets them sell into negative anyway (Android parity).
                const blocked = p.stock <= 0 && p.stockable !== false && !stockMinus;
                return (
                  <button key={p.id} className="b2-prod" disabled={blocked} onClick={() => pickProduct(p)}>
                    {showImages && p.image && (
                      <img src={p.image} alt="" style={{ width: '100%', height: 42, objectFit: 'cover', borderRadius: 4 }} />
                    )}
                    {!(showImages && p.image && hideItemName) && <span className="b2-prod-name">{prodLabel(p)}</span>}
                    <span className="b2-prod-price">{money(p.price)}</span>
                    <span className={`b2-prod-stock ${p.stock <= 10 ? 'low' : ''}`}>
                      {p.stockable === false ? 'Service' : p.stock <= 0 ? (blocked ? 'Out of stock' : `Stk ${p.stock}`) : `Stk ${p.stock}`}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* bill details — order per bill_fragment.xml: Payment Mode, customer card, discount, totals.
            Hidden unless App Setting → Billing Screen Option → Bill Details Section is on. */}
        {showBillDetails && (
        <div className="b2-details card-rounded">
          {/* payment mode (first) */}
          <div className="b2-paymode">
            <span className="b2-paymode-lbl">PAYMENT MODE :</span>
            <div className="b2-paymode-box">
              <span className="b2-rs">{cur}</span>
              <select value={payMode} onChange={(e) => setPayMode(e.target.value as PayMode)}>
                <option>Cash</option>
                <option>Credit</option>
                <option>Digital</option>
                <option>Split</option>
              </select>
            </div>
          </div>

          {/* customer details card (orange) — display of selected customer */}
          <div className="b2-customer card-orange">
            {customerName ? (
              <div className="b2-cust-info">
                <span className="b2-cust-name">{customerName}</span>
                {selectedCustomer?.phone && <span className="b2-cust-contact">{selectedCustomer.phone}</span>}
                {selectedCustomer?.creditBalance ? (
                  <span className="b2-cust-balance">BAL: {money(selectedCustomer.creditBalance)}</span>
                ) : null}
                {advanceOn && selectedCustomer && (
                  <button
                    type="button"
                    className="b2-coupon-btn"
                    onClick={takeAdvance}
                    title="Record a customer advance"
                  >
                    Advance +
                  </button>
                )}
                <span className="b2-cust-x" onClick={() => setCustomerName('')}>
                  ✕
                </span>
              </div>
            ) : (
              <span className="b2-cust-empty">Customer</span>
            )}
          </div>

          {/* discount row */}
          {showDiscountUi && (
          <>
          <div className="b2-three-head">
            <span>DISCOUNT AMT</span>
            <span>DISCOUNT PER</span>
            <span>{cur} DELIVERY CHARGES</span>
          </div>
          <div className="b2-three">
            <div className="b2-inputcell">
              <input placeholder={`${cur} 00.00`} value={discAmt} onChange={(e) => setDiscAmt(e.target.value)} />
              <span className="b2-cell-refresh" onClick={() => setDiscAmt('')}>
                ⟳
              </span>
            </div>
            <div className="b2-inputcell">
              <input placeholder="% 00.00" value={discPct} onChange={(e) => setDiscPct(e.target.value)} />
              <span className="b2-cell-refresh" onClick={() => setDiscPct('')}>
                ⟳
              </span>
            </div>
            <div className="b2-inputcell">
              <input placeholder={`${cur} 00.00`} value={deliveryCharges} onChange={(e) => setDeliveryCharges(e.target.value)} />
              <span className="b2-cell-refresh" onClick={() => setDeliveryCharges('')}>
                ⟳
              </span>
            </div>
          </div>
          </>
          )}

          {/* "13. Discount To Directly Add Bill" — one-tap % chips */}
          {quickDiscount && (
            <div className="b2-quickdisc">
              <span>QUICK DISC :</span>
              {[2, 5, 10].map((n) => (
                <button
                  key={n}
                  className={parseFloat(discPct) === n ? 'on' : ''}
                  onClick={() => setDiscPct(String(n))}
                >
                  {n}%
                </button>
              ))}
              <button onClick={() => setDiscPct('')}>✕</button>
            </div>
          )}

          {/* service charge row (restaurant) */}
          {serviceChargeOn && (
            <>
              <div className="b2-three-head">
                <span>SERVICE CHARGE %</span>
                <span>{cur} SERVICE CHARGE</span>
                <span>{cur} EXTRA CHARGES</span>
              </div>
              <div className="b2-three">
                <div className="b2-inputcell">
                  <input placeholder="% 00.00" value={servicePct} onChange={(e) => setServicePct(e.target.value)} />
                  <span className="b2-cell-refresh" onClick={() => setServicePct('')}>⟳</span>
                </div>
                <div className="b2-inputcell">
                  <input placeholder={`${cur} 00.00`} value={serviceAmt} onChange={(e) => setServiceAmt(e.target.value)} />
                  <span className="b2-cell-refresh" onClick={() => setServiceAmt('')}>⟳</span>
                </div>
                <div className="b2-inputcell">
                  <input placeholder={`${cur} 00.00`} value={extraCharges} onChange={(e) => setExtraCharges(e.target.value)} />
                  <span className="b2-cell-refresh" onClick={() => setExtraCharges('')}>⟳</span>
                </div>
              </div>
            </>
          )}

          {/* delivery challan / vehicle */}
          {deliveryNoteOn && (
            <>
              <div className="b2-three-head">
                <span>DELIVERY CHALLAN NO</span>
                <span>VEHICLE NO</span>
                <span />
              </div>
              <div className="b2-three">
                <div className="b2-inputcell">
                  <input placeholder="Challan No" value={challanNo} onChange={(e) => setChallanNo(e.target.value)} />
                </div>
                <div className="b2-inputcell">
                  <input placeholder="Vehicle No" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} />
                </div>
                <div />
              </div>
            </>
          )}

          {/* estimate (proforma) toggle */}
          {estimateModeOn && (
            <label className="b2-estimate">
              <input type="checkbox" checked={estimate} onChange={(e) => setEstimate(e.target.checked)} />
              <span>Estimate bill (no GST breakup)</span>
            </label>
          )}

          {/* per-bill Non-GST / IGST overrides (Android print-button 28 / 30) */}
          {nonGstMode && (
            <label className="b2-estimate">
              <input type="checkbox" checked={effNonGst} onChange={(e) => setNonGstBill(e.target.checked)} />
              <span>Non-GST bill (Bill of Supply)</span>
            </label>
          )}
          {igstMode && (
            <label className="b2-estimate">
              <input type="checkbox" checked={effIgst} onChange={(e) => setIgstBill(e.target.checked)} />
              <span>IGST bill (inter-state)</span>
            </label>
          )}

          {/* coupon row (shown once any coupons exist) */}
          {coupons.length > 0 && (
            <div className="b2-coupon">
              <input
                placeholder={multiCouponOn ? 'Coupon code (multi)' : 'Coupon code'}
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
                disabled={!!appliedCoupon && !multiCouponOn}
              />
              {(!appliedCoupon || multiCouponOn) && (
                <button className="b2-coupon-btn" onClick={applyCoupon}>Apply</button>
              )}
              {appliedCoupon && (
                <button className="b2-coupon-btn clear" onClick={clearCoupon}>Remove</button>
              )}
              {couponMsg && (
                <span className={`b2-coupon-msg ${appliedCoupon ? 'ok' : 'err'}`}>{couponMsg}</span>
              )}
            </div>
          )}

          {/* totals */}
          <div className="b2-totrow">
            <span>Bill Amt.</span>
            <span className="b2-totval">{money(totals.billAmt)}</span>
            <span className="b2-spacer" />
          </div>
          <div className="b2-totrow">
            <span>Discount Amt.{slabPct > 0 ? ` (auto ${slabPct}% slab)` : ''}</span>
            <span className="b2-totval">{money(totals.discount)}</span>
            <span className="b2-spacer" />
          </div>
          {totals.serviceCharge > 0 && (
            <div className="b2-totrow">
              <span>Service Charge</span>
              <span className="b2-totval">{money(totals.serviceCharge)}</span>
              <span className="b2-spacer" />
            </div>
          )}
          {totals.extraCharges > 0 && (
            <div className="b2-totrow">
              <span>Extra Charges</span>
              <span className="b2-totval">{money(totals.extraCharges)}</span>
              <span className="b2-spacer" />
            </div>
          )}
          <div className="b2-totrow">
            <span>{igstMode ? 'IGST' : 'Tax (CGST+SGST)'}{!gstFlag ? ' (N/A)' : ''}</span>
            <span className="b2-totval">{money(totals.tax)}</span>
            <span className="b2-spacer" />
          </div>
          {totals.cess > 0 && (
            <div className="b2-totrow">
              <span>CESS</span>
              <span className="b2-totval">{money(totals.cess)}</span>
              <span className="b2-spacer" />
            </div>
          )}
          {orderType === 'parcel' && (
            <div className="b2-totrow">
              <span>Delivery Charges</span>
              <span className="b2-totval">{money(totals.delivery)}</span>
              <span className="b2-spacer" />
            </div>
          )}
          <div className="b2-divider" />
          <div className="b2-totrow final">
            <span>Final Total</span>
            <span className="b2-totval">{money(totals.finalTotal)}</span>
            <span className="b2-spacer" />
          </div>

          <input className="b2-remark" placeholder="Bill Remark" value={remark} onChange={(e) => setRemark(e.target.value)} />
        </div>
        )}
      </div>

      {/* Table picker (ask_table_details_dialog) — type a number or tap a tile */}
      {showTablePicker && (
        <div className="b2-modal-overlay" onClick={() => setShowTablePicker(false)}>
          <div className="b2-table-modal" onClick={(e) => e.stopPropagation()}>
            <div className="b2-table-modal-head">
              <span>Add Table</span>
              <span className="b2-modal-x" onClick={() => setShowTablePicker(false)}>
                ✕
              </span>
            </div>
            {/* "G - Show Premises First In Billing Screen" */}
            {showPremisesFirst && premises.length > 0 && (
              <div className="b2-cats" style={{ padding: '6px 10px' }}>
                <span className="b2-prem-lbl">PREMISE :</span>
                {premises.map((pr) => (
                  <button
                    key={pr}
                    className={`b2-cat ${premise === pr ? 'active' : ''}`}
                    onClick={() => setPremise(premise === pr ? '' : pr)}
                  >
                    {pr}
                  </button>
                ))}
              </div>
            )}
            <div className="b2-table-modal-search">
              <input
                autoFocus
                placeholder="Table Number"
                value={tableInput}
                onChange={(e) => setTableInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && pickTable(tableInput + tableSuffix)}
              />
              <button className="b2-table-add-btn" onClick={() => pickTable(tableInput + tableSuffix)}>
                Add
              </button>
            </div>
            {/* "I - ABCD Table Split" — split one physical table into A–D seats */}
            {abcdSplit && (
              <div className="b2-cats" style={{ padding: '6px 10px' }}>
                <span className="b2-prem-lbl">SPLIT :</span>
                {['', 'A', 'B', 'C', 'D'].map((sfx) => (
                  <button
                    key={sfx || 'none'}
                    className={`b2-cat ${tableSuffix === sfx ? 'active' : ''}`}
                    onClick={() => setTableSuffix(sfx)}
                  >
                    {sfx || '—'}
                  </button>
                ))}
              </div>
            )}
            <div className="b2-table-grid">
              {pickerTables.map((t) => {
                const name = t + tableSuffix;
                const status = tableStatus(name);
                return (
                  <button
                    key={t}
                    className={`b2-table-tile ${status} ${table === name ? 'active' : ''}`}
                    onClick={() => pickTable(name)}
                  >
                    {name}
                    {status !== 'free' && <span className="b2-table-tile-dot" />}
                  </button>
                );
              })}
            </div>
            <div className="b2-table-legend">
              <span className="b2-table-legend-item">
                <span className="b2-legend-swatch occupied" /> Occupied (KOT sent)
              </span>
              <span className="b2-table-legend-item">
                <span className="b2-legend-swatch booked" /> Booked
              </span>
              <span className="b2-table-legend-item">
                <span className="b2-legend-swatch free" /> Free
              </span>
            </div>
          </div>
        </div>
      )}

      {showPayment && pendingTx && <PaymentModal transaction={pendingTx} onComplete={onPaid} onCancel={() => setShowPayment(false)} />}

      {showCustPicker && (
        <CustomerPickerDialog
          onSelect={(c) => {
            setCustomerName(c.name);
            setShowCustPicker(false);
          }}
          onClose={() => setShowCustPicker(false)}
        />
      )}

      {/* Transport details ("F - Transport Details", transport_details.xml) */}
      {showTransport && (
        <div className="b2-modal-overlay" onClick={() => setShowTransport(false)}>
          <div className="b2-table-modal" onClick={(e) => e.stopPropagation()}>
            <div className="b2-table-modal-head">
              <span>Transport Details</span>
              <span className="b2-modal-x" onClick={() => setShowTransport(false)}>
                ✕
              </span>
            </div>
            <div className="b2-transport-form">
              {(
                [
                  ['vehicleNo', 'Vehicle No *'],
                  ['driverName', 'Driver Name'],
                  ['destination', 'Destination'],
                  ['outTime', 'Out Time'],
                  ['inTime', 'In Time'],
                  ['distance', 'Distance (km)'],
                  ['deliveryNote', 'Delivery Note'],
                ] as [keyof TransportInfo, string][]
              ).map(([k, label]) => (
                <label key={k}>
                  {label}
                  <input
                    value={(transport?.[k] as string) ?? ''}
                    onChange={(e) =>
                      setTransport({ ...(transport ?? { vehicleNo: '' }), [k]: e.target.value })
                    }
                  />
                </label>
              ))}
              <label>
                Delivered?
                <select
                  value={transport?.deliveryStatus ?? ''}
                  onChange={(e) =>
                    setTransport({
                      ...(transport ?? { vehicleNo: '' }),
                      deliveryStatus: e.target.value as TransportInfo['deliveryStatus'],
                    })
                  }
                >
                  <option value="">—</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </label>
            </div>
            <div className="b2-transport-actions">
              <button className="b2-coupon-btn clear" onClick={() => setTransport(null)}>
                Clear
              </button>
              <button className="b2-table-add-btn" onClick={() => setShowTransport(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-pay actions ("3. Show Print Buttons After Pay" / "12. Bill Send To
          Email" / "16. A4 Delivery Note") */}
      {lastPaid && (
        <div className="b2-modal-overlay" onClick={() => setLastPaid(null)}>
          <div className="b2-table-modal b2-postpay" onClick={(e) => e.stopPropagation()}>
            <div className="b2-table-modal-head">
              <span>Bill {lastPaid.billNo} saved — {money(lastPaid.total)}</span>
              <span className="b2-modal-x" onClick={() => setLastPaid(null)}>
                ✕
              </span>
            </div>
            <div className="b2-postpay-btns">
              {showPrintAfterPay && (
                <button
                  onClick={() =>
                    printReceiptAuto(lastPaid, config).catch((e) => console.warn('Print failed:', e))
                  }
                >
                  🖨 Print Bill
                </button>
              )}
              {emailBillOn && (
                <a href={mailtoBillUrl(lastPaid, config)} className="b2-postpay-link">
                  ✉ Email Bill
                </a>
              )}
              {a4Note && (lastPaid.deliveryChallanNo || lastPaid.vehicleNo) && (
                <button onClick={() => printDeliveryNote(lastPaid, config)}>📄 Delivery Note (A4)</button>
              )}
              <button onClick={() => setLastPaid(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Combo picker ("B. Combo Add In Billing" lists the Recipe Master) */}
      {showCombos && (
        <div className="b2-modal-overlay" onClick={() => setShowCombos(false)}>
          <div className="b2-variant-modal" onClick={(e) => e.stopPropagation()}>
            <div className="b2-variant-head">Combos — choose one</div>
            <div className="b2-variant-list">
              {recipes.map((r) => {
                const p = products.find((x) => x.id === r.productId);
                if (!p) return null;
                return (
                  <button
                    key={r.id}
                    className="b2-variant-btn"
                    onClick={() => {
                      addProduct(p);
                      setShowCombos(false);
                    }}
                  >
                    <span>{r.name}</span>
                    <span>{money(p.price)}</span>
                  </button>
                );
              })}
            </div>
            <button className="b2-variant-cancel" onClick={() => setShowCombos(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* "B - Floating Keyboard Button" + on-screen keypad */}
      {floatingKeyboard && (
        <>
          <button className="b2-fab" onClick={() => setShowKeypad((v) => !v)} title="Floating keyboard">
            ⌨
          </button>
          {showKeypad && (
            <div className="b2-keypad">
              {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '⌫'].map((k) => (
                <button key={k} onClick={() => keypadPress(k)}>
                  {k}
                </button>
              ))}
              <button className="enter" onClick={() => keypadPress('↵')}>
                ↵ Enter
              </button>
            </div>
          )}
        </>
      )}

      {/* camera barcode scanner */}
      {showScanner && (
        <BarcodeScanner onDetected={handleScan} onClose={() => setShowScanner(false)} />
      )}

      {/* variant chooser */}
      {variantPick && (
        <div className="b2-modal-overlay" onClick={() => setVariantPick(null)}>
          <div className="b2-variant-modal" onClick={(e) => e.stopPropagation()}>
            <div className="b2-variant-head">
              {variantPick.product.name} — choose variant
              {variantPick.qty > 1 && ` (×${variantPick.qty})`}
            </div>
            <div className="b2-variant-list">
              {variantPick.product.variants!.map((v) => (
                <button
                  key={v.id}
                  className="b2-variant-btn"
                  onClick={() => chooseVariant(variantPick.product, v, variantPick.qty)}
                >
                  <span>{v.name}</span>
                  <span>{money(v.price)}</span>
                </button>
              ))}
            </div>
            <button className="b2-variant-cancel" onClick={() => setVariantPick(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bill;
